import { decodeHTMLEntities } from '@skin-seoul/utils';
import { groupBy, mapValues } from 'es-toolkit';
import { ProductStockStatus, ProductCategory } from '../constants';
import type { Product } from '../model';

export interface CategoryOption {
  name: string;
}

interface CategoryProductPair {
  category: string;
  product: Product;
}

/**
 * Processes products data to extract available categories
 * @param products - Array of products to process
 * @param minProductsPerCategory - Minimum number of products required for a category to be included (default: 9)
 * @returns Array of categories that have products in the collection
 */
// Avoid magic numbers by naming the default threshold explicitly
const DEFAULT_MIN_PRODUCTS_PER_CATEGORY = 9 as const;

const decodeCategoryName = (category: ProductCategory): string => {
  return decodeHTMLEntities(String(category));
};

export const getAvailableCategorizedProducts = (
  products: Product[] = [],
  minProductsPerCategory: number = DEFAULT_MIN_PRODUCTS_PER_CATEGORY,
): CategoryOption[] => {
  const inStock = products.filter((p) => p.status === ProductStockStatus.instock);
  if (inStock.length === 0) {
    return [];
  }

  // Aggregate counts per decoded category name
  const categoryCount = inStock.reduce<Record<string, number>>((acc, product) => {
    if (!Array.isArray(product.category)) {
      return acc;
    }

    product.category.forEach((category) => {
      const name = decodeCategoryName(category);
      acc[name] = (acc[name] ?? 0) + 1;
    });

    return acc;
  }, {});

  return transformCategoryCountToOptions(categoryCount, minProductsPerCategory);
};

/**
 * Groups products by category and returns an object where the key is the decoded category name
 * and the value is an array of products belonging to that category. Categories with fewer than
 * `minProductsPerCategory` items are omitted.
 */
export const groupProductsByCategory = (
  products: Product[] = [],
  minProductsPerCategory: number = DEFAULT_MIN_PRODUCTS_PER_CATEGORY,
): Record<string, Product[]> => {
  const inStock = products.filter((p) => p.status === ProductStockStatus.instock);
  if (inStock.length === 0) {
    return {};
  }

  const categoryProductPairs: CategoryProductPair[] = inStock.flatMap((product) =>
    (Array.isArray(product.category) ? product.category : []).map((category) => ({
      category: decodeCategoryName(category),
      product,
    })),
  );

  const grouped = groupBy(categoryProductPairs, (pair) => pair.category);

  const filtered = Object.fromEntries(
    Object.entries(grouped).filter(([, arr]) => arr.length >= minProductsPerCategory),
  );

  return mapValues(filtered, (arr) => arr.map((pair) => pair.product));
};

/**
 * Internal utility: converts categoryCount map to CategoryOption[]
 */
const transformCategoryCountToOptions = (
  categoryCount: Record<string, number>,
  minProductsPerCategory: number,
): CategoryOption[] => {
  return Object.entries(categoryCount)
    .filter(([, count]) => count >= minProductsPerCategory)
    .map(([name]) => ({ name }));
};

import type { TaxonomyTerm } from '../model';

/**
 * Recursively builds the full path by finding all parent categories
 * @param category - Current category term
 * @param categoryTaxonomy - Array of TaxonomyTerm from the API
 * @param path - Accumulated path segments (built from child to parent)
 * @returns Array of slug segments from root to leaf
 */
const buildCategoryPath = (category: TaxonomyTerm, categoryTaxonomy: TaxonomyTerm[], path: string[] = []): string[] => {
  // Add current category slug to the beginning of path
  const currentPath = [category.slug, ...path];

  // If no parent, we're at the root - return the complete path
  if (!category.parentTermId) {
    return currentPath;
  }

  // Find parent and recurse up the tree
  const parent = categoryTaxonomy.find((c) => c.termId === category.parentTermId);
  if (!parent) {
    return currentPath;
  }

  return buildCategoryPath(parent, categoryTaxonomy, currentPath);
};

/**
 * Builds the collection URL for a category using taxonomy data
 * Handles multi-level parent/child relationships recursively
 *
 * @param categoryName - The display name of the category (e.g., "Men's Facial Cleanser")
 * @param categoryTaxonomy - Array of TaxonomyTerm from the API
 * @returns The collection URL path (e.g., "/collections/mens/mens-skincare/mens-facial-cleanser/")
 */
export const buildCategoryCollectionUrl = (categoryName: string, categoryTaxonomy: TaxonomyTerm[]): string => {
  const category = categoryTaxonomy.find((c) => c.name === categoryName);
  if (!category) {
    return '/shop/';
  }

  const pathSegments = buildCategoryPath(category, categoryTaxonomy);
  return `/collections/${pathSegments.join('/')}/`;
};
